import { useMemo } from 'react';

import { fuzzyMatch, useSearchAndCollapse } from '@hooks/use-search-and-collapse';

import { IntegrationPollingType } from 'api/integrations/requests';

export const useIntegrationSearch = (integrations: IntegrationPollingType[]) => {
  const processedIntegrations = useMemo(() => {
    return integrations
      .map((integration) => ({
        ...integration,
        endpoints: integration.endpoints.filter(
          (endpoint) => endpoint.showInPolling && !endpoint.isUsedInJourney
        ),
      }))
      .filter((integration) => integration.endpoints.length > 0);
  }, [integrations]);

  const {
    searchStr,
    setSearchStr,
    filteredData,
    collapsedState,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  } = useSearchAndCollapse({
    data: processedIntegrations,
    getGroupKey: (integration) => integration.name,
    searchFilter: (integration, searchLower) => {
      if (fuzzyMatch(integration.name, searchLower)) {
        return true;
      }
      return integration.endpoints.some((endpoint) => fuzzyMatch(endpoint.name, searchLower));
    },
    localStorageKey: 'polling-integration-search',
  });

  const visibleIntegrations = useMemo(() => {
    if (!searchStr.trim()) {
      return filteredData;
    }

    return filteredData.map((integration) => {
      if (fuzzyMatch(integration.name, searchStr)) {
        return integration;
      }

      const filteredEndpoints = integration.endpoints.filter((endpoint) =>
        fuzzyMatch(endpoint.name, searchStr)
      );

      return {
        ...integration,
        endpoints: filteredEndpoints,
      };
    });
  }, [filteredData, searchStr]);

  return {
    searchStr,
    setSearchStr,
    visibleIntegrations,
    collapsedState,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  };
};
